<!-- eslint-disable vue/no-template-shadow -->
<!--
  WebSocketTest — Pineapple 톤
  웹소켓 연결 및 미리보기 데이터 테스트 도구
-->
<template>
  <div class="p-page">
    <a-page-head title="WebSocket 테스트" :breadcrumbs="[{ label: '테스트' }, { label: 'WebSocket' }]">
    </a-page-head>

    <a-card title="설정" padding="20">
      <div class="p-form-grid">
        <div class="p-field p-field--wide">
          <label class="p-field__label">📛 Client ID</label>
          <input v-model="clientId" class="p-input" placeholder="예: section_test@example.com" />
        </div>
        <div class="p-field p-field--wide">
          <label class="p-field__label">🔗 WebSocket 주소</label>
          <input v-model="websocketUrl" class="p-input p-input--mono" readonly />
        </div>
        <div class="p-field">
          <label class="p-field__label">📕 바코드</label>
          <input v-model="barcode" class="p-input p-input--mono" />
        </div>
        <div class="p-field">
          <label class="p-field__label">📖 챕터</label>
          <input v-model.number="chapterNumber" type="number" min="1" class="p-input" />
        </div>
        <div class="p-field">
          <label class="p-field__label">📘 섹션</label>
          <input v-model.number="uniqueKey" type="number" min="1" class="p-input" />
        </div>
        <div class="p-field p-field--wide" style="grid-column: span 3;">
          <label class="p-field__label">📝 전송 메시지 (Preview)</label>
          <textarea v-model="previewJson" class="p-input p-input--area" rows="5" />
        </div>
      </div>

      <div class="p-actions">
        <a-btn kind="primary" size="md" @click="connectWebSocket">웹소켓 연결</a-btn>
        <a-btn kind="yellow" size="md" :disabled="!isConnected" @click="sendPreview">미리보기 전송</a-btn>
        <a-btn kind="danger" size="md" :disabled="!isConnected" @click="disconnectWebSocket">연결 종료</a-btn>
        <a-btn kind="ghost" size="md" @click="clearLogs">로그 초기화</a-btn>
        <a-pill :kind="isConnected ? 'ok' : 'default'" style="margin-left: auto;">
          {{ isConnected ? '연결됨' : '연결 안됨' }}
        </a-pill>
      </div>
    </a-card>

    <a-card title="📄 로그" padding="0" class="p-mt-16">
      <div class="p-logs">
        <pre v-if="!logs.length" class="p-log-empty">로그가 없습니다.</pre>
        <pre v-for="(log, idx) in logs" :key="idx" class="p-log" v-text="log" />
      </div>
    </a-card>
  </div>
</template>

<script>
import APageHead from '@/components/Pineapple/APageHead.vue';
import ACard from '@/components/Pineapple/ACard.vue';
import APill from '@/components/Pineapple/APill.vue';
import ABtn from '@/components/Pineapple/ABtn.vue';

export default {
  name: 'WebSocketPreviewTest',
  components: { APageHead, ACard, APill, ABtn },
  data() {
    return {
      socket: null,
      clientId: 'section_test@example.com',
      barcode: '9781635190151',
      chapterNumber: 1,
      uniqueKey: 1,
      isConnected: false,
      logs: [],
    };
  },
  computed: {
    websocketUrl() {
      return `${this.$CONF.API_URL.replace(/^http/, 'ws')}/textbook/ws/${this.clientId}`;
    },
    previewJson() {
      return JSON.stringify({
        barcode: this.barcode,
        chapterNumber: this.chapterNumber,
        uniqueKey: this.uniqueKey,
      }, null, 2);
    },
  },
  beforeUnmount() { this.disconnectWebSocket(); },
  methods: {
    connectWebSocket() {
      if (this.isConnected) this.disconnectWebSocket();
      try {
        this.socket = new WebSocket(this.websocketUrl);
        this.socket.onopen = () => {
          this.isConnected = true;
          this.log(`✅ 연결됨: ${this.websocketUrl}`);
        };
        this.socket.onmessage = (e) => { this.log(`📥 수신됨: ${e.data}`); };
        this.socket.onerror = (e) => { this.log(`❌ 에러 발생: ${e.message || 'Unknown Error'}`); };
        this.socket.onclose = (e) => {
          this.isConnected = false;
          this.log(`🔌 연결 종료 (코드: ${e.code})`);
        };
      } catch (e) {
        this.log(`❗ 연결 실패: ${e.message}`);
      }
    },
    disconnectWebSocket() {
      if (this.socket) this.socket.close(1000, '연결 종료 요청');
    },
    sendPreview() {
      if (!this.socket || !this.isConnected) {
        this.log('⚠️ 웹소켓이 연결되어 있지 않습니다.');
        return;
      }
      try {
        const msg = {
          type: 'preview_request',
          barcode: this.barcode,
          chapterNumber: this.chapterNumber,
          uniqueKey: this.uniqueKey,
        };
        this.socket.send(JSON.stringify(msg));
        this.log(`📤 전송됨: ${JSON.stringify(msg)}`);
      } catch (e) {
        this.log(`❌ 전송 실패: ${e.message}`);
      }
    },
    clearLogs() { this.logs = []; },
    log(message) {
      const now = new Date().toLocaleTimeString();
      let formatted = message;
      try {
        if (typeof message === 'object') formatted = JSON.stringify(message, null, 2);
        else if (typeof message === 'string' && message.trim().startsWith('{')) {
          formatted = JSON.stringify(JSON.parse(message), null, 2);
        }
      } catch (e) { /* ignore */ }
      this.logs.unshift(`[${now}]\n${formatted}`);
      if (this.logs.length > 100) this.logs.pop();
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/scss/_pineapple.scss';

.p-page {
  font-family: $p-font-body;
  color: $p-ink;
}
.p-mt-16 { margin-top: 16px; }

.p-form-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 12px;
  margin-bottom: 16px;

  @media (max-width: 900px) { grid-template-columns: 1fr; }
}
.p-field {
  display: flex; flex-direction: column; gap: 6px;
  min-width: 0;

  &--wide { grid-column: span 3; @media (max-width: 900px) { grid-column: span 1; } }

  &__label {
    font-size: 10px; font-weight: 800;
    letter-spacing: 0.1em; text-transform: uppercase;
    color: $p-ink-soft;
  }
}
.p-input {
  width: 100%; height: 36px;
  padding: 0 12px;
  border: 1px solid $p-line; border-radius: 8px;
  background: $p-panel;
  font-family: $p-font-body; font-size: 12px; color: $p-ink;
  outline: none;

  &:focus { border-color: $p-point; box-shadow: 0 0 0 3px rgba(255,201,60,0.2); }
  &--mono { font-family: $p-font-mono; font-size: 11px; }
  &--area {
    height: auto; padding: 10px 12px; resize: vertical;
    font-family: $p-font-mono; font-size: 11px;
    background: $p-panel-alt;
  }
}
.p-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  align-items: center;
}

.p-logs {
  max-height: 360px;
  overflow: auto;
  padding: 12px;
  background: $p-sidebar;
  color: $p-sidebar-tx;
  border-radius: 0 0 12px 12px;
  font-family: $p-font-mono;
  font-size: 11px;

  &::-webkit-scrollbar { width: 8px; }
  &::-webkit-scrollbar-thumb { background: $p-sidebar-alt; border-radius: 4px; }
}
.p-log {
  margin: 0 0 8px;
  white-space: pre-wrap;
  word-break: break-all;
  color: $p-sidebar-tx;
}
.p-log-empty {
  margin: 0;
  text-align: center;
  padding: 24px;
  color: $p-sidebar-mid;
}
</style>
